import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { intlShape, injectIntl } from 'react-intl';
import momentPropTypes from 'react-moment-proptypes';
import { DateRangeSelectorInputs, withPriceData, withAvailabilityData, getAvailabilityFetcher, getCalendarPrices } from '@kognitiv/bf-library';
import moment from 'moment';
import { URLService } from 'Services';

const isShowingPrices = (calendarPricesMode) => [ 'FAIR', 'CHEAPEST' ].includes(calendarPricesMode);

const sanitizeCalendarPricesMode = (mode) => [ 'CHEAPEST', 'FAIR', 'NONE' ].includes(mode)
    ? mode
    : 'NONE';

const getFetch = ({
    isMultiProperty, occupancy, calendarPricesMode, currencyCode, propertyCode, globalMinOccupancy, priceCalcMode, channelId,
}) => {
    if (isMultiProperty) {
        return null;
    }

    if (isShowingPrices(calendarPricesMode)) {
        const url = URLService.externals.getCalendarPricesURL();
        const calculateTaxes = !priceCalcMode ||
            [ 'withTaxesAndFees', 'withInformativeTaxesAndFees' ].includes(priceCalcMode);
        const includeInformativeTaxesAndFees = !priceCalcMode ||
            [ 'asInPricelist', 'withInformativeTaxesAndFees' ].includes(priceCalcMode);

        return getCalendarPrices({
            url,
            currencyCode,
            occupancy,
            propertyCode,
            includeInformativeTaxesAndFees,
            calculateTaxes,
            channelId,
            priceMode: calendarPricesMode,
        });
    } else {
        const url = URLService.externals.getAvailabilitiesURL(propertyCode);

        return getAvailabilityFetcher({
            url,
            occupancy,
            globalMinOccupancy,
        });
    }
};

const copyMoment = (mom) => {
    if (mom) {
        return typeof mom === 'string'
            ? moment(mom, 'YYYY-MM-DD')
            : moment(mom.format('YYYY-MM-DD'), 'YYYY-MM-DD');
    }

    return null;
};


// i18nLabel[bf.displayMode.totalPrice]
// i18nLabel[bf.displayMode.avgPricePerNight]
// i18nLabel[bf.displayMode.avgPerPersonPerNight]
// i18nLabel[bf.checkIn]
// i18nLabel[bf.checkOut]
// i18nLabel[bf.stayDates]
// i18nLabel[bf.arrivalsOnly]
// i18nLabel[bf.departuresOnly]
// i18nLabel[bf.noAvailability]
// i18nLabel[bf.price.from]
// i18nLabel[bf.minLosTooltip]
// i18nLabel[bf.maxLosTooltip]
// i18nLabel[hotel.rateManager.ratePlans.addEditRatePlan.closeForDeparture.label]
// i18nLabel[bf.doneButtonDatePicker]
// i18nLabel[seekda.chainmetacpc.reset]
// i18nLabel[bf.cancel]
// i18nLabel[bf.offerUnavailableForNights]
// i18nLabel[bf.seeFlexibleDates]
const DataFetchingDateRangeSelector = ({ showInputs, globalMinOccupancy,
    currentLanguage, checkIn, checkOut, maxLengthOfStay, minLengthOfStay, channelId,
    minNightOffset, onChange, maxCheckOutDate, notAvailableDates, priceCalcMode,
    openFlexibleDatesCalendar, flexibleDatesEnabled, currencyDisplay, currencySymbol, currencyCode,
    isMultiProperty, calendarPricesMode, occupancy, propertyCode, currentRoom, intl }) => {

    const [ priceMode, setPriceMode ] = useState(sanitizeCalendarPricesMode(calendarPricesMode));
    const [ initialStartDate, setInitialStartDate  ] = useState(copyMoment(checkIn));
    const [ initialEndDate, setInitialEndDate ] = useState(copyMoment(checkOut));

    const createCalendarComponent = useCallback(( isMultiProperty, calendarPricesMode ) => {
        if (isMultiProperty) {
            return DateRangeSelectorInputs;
        }

        const cal = isShowingPrices(calendarPricesMode)
            ? withPriceData(DateRangeSelectorInputs)
            : withAvailabilityData(DateRangeSelectorInputs);

        return cal;
    }, [ isMultiProperty, calendarPricesMode ]);

    const createRepoId = useCallback(
        ( propertyCode, occupancy, calendarPricesMode, priceCalcMode, currencyCode ) =>
            `cal-${calendarPricesMode}-${priceCalcMode}-${currencyCode}-${propertyCode}-${occupancy}`,
    );

    const optionalCalendarProps = isMultiProperty
        ? { notAvailableDates }
        : {};

    const fetch = getFetch({
        isMultiProperty,
        channelId,
        occupancy,
        priceMode,
        propertyCode,
        currencyCode,
        currentRoom,
        calendarPricesMode,
        globalMinOccupancy,
        priceCalcMode,
    });

    const [ repoId, setRepoId ] = useState(createRepoId(propertyCode, occupancy, priceMode, priceCalcMode, currencyCode));

    useEffect(() => {
        setInitialStartDate(copyMoment(checkIn));
    }, [ checkIn ]);

    useEffect(() => {
        setInitialEndDate(copyMoment(checkOut));
    }, [ checkOut ]);

    useEffect(() => {
        setPriceMode(sanitizeCalendarPricesMode(calendarPricesMode));
    }, [ calendarPricesMode ]);

    useEffect(() => {
        setRepoId(createRepoId(propertyCode, occupancy, priceMode, priceCalcMode, currencyCode));
    }, [
        isMultiProperty,
        occupancy,
        calendarPricesMode,
        propertyCode,
        currencyCode,
        priceCalcMode,
        priceMode,
    ]);

    if (moment.locale() !== currentLanguage) {
        moment.locale(currentLanguage);
    }

    const CalendarComponent = createCalendarComponent(isMultiProperty, priceMode);


    return <div className="search-bar-input-wrapper-custom-class">
        <CalendarComponent
            showInputs={showInputs}
            currentLanguage={currentLanguage}
            checkIn={checkIn}
            checkOut={checkOut}
            initialStartDate={initialStartDate}
            initialEndDate={initialEndDate}
            maxLengthOfStay={maxLengthOfStay}
            minLengthOfStay={minLengthOfStay}
            minNightOffset={minNightOffset}
            onChange={onChange}
            maxCheckOutDate={maxCheckOutDate}
            openFlexibleDatesCalendar={openFlexibleDatesCalendar}
            flexibleDatesEnabled={flexibleDatesEnabled}
            isMultiProperty={isMultiProperty}
            calendarPricesMode={calendarPricesMode}
            occupancy={occupancy}
            propertyCode={propertyCode}
            fetch={fetch}
            repoIdentifier={repoId}
            currencyDisplay={currencyDisplay}
            currencyCode={currencyCode}
            currencySymbol={currencySymbol}
            showCancelButton={false}
            intl={intl}
            {...optionalCalendarProps}
        ></CalendarComponent>
    </div>;
};


DataFetchingDateRangeSelector.propTypes = {
    showInputs: PropTypes.bool,
    currentLanguage: PropTypes.string,
    checkIn: momentPropTypes.momentObj,
    checkOut: momentPropTypes.momentObj,
    maxLengthOfStay: PropTypes.number,
    minLengthOfStay: PropTypes.number,
    minNightOffset: PropTypes.number,
    onChange: PropTypes.func,
    maxCheckOutDate: PropTypes.string,
    notAvailableDates: PropTypes.array,
    openFlexibleDatesCalendar: PropTypes.bool,
    flexibleDatesEnabled: PropTypes.bool,
    currencyDisplay: PropTypes.oneOf([ 'symbol', 'code' ]),
    currencyCode: PropTypes.string,
    currencySymbol: PropTypes.string,
    isMultiProperty: PropTypes.bool,
    calendarPricesMode: PropTypes.string,
    occupancy: PropTypes.string,
    propertyCode: PropTypes.string,
    currentRoom: PropTypes.number,
    globalMinOccupancy: PropTypes.number,
    intl: intlShape,
    channelId: PropTypes.string,
    priceCalcMode: PropTypes.oneOf([ 'asInPricelist', 'withInformativeTaxesAndFees', 'excludingTaxesAndFees', 'withTaxesAndFees' ]),
};

DataFetchingDateRangeSelector.defaultProps = {
    priceCalcMode: 'asInPricelist',
    currentRoom: 0,
};

export default injectIntl(DataFetchingDateRangeSelector);
